/**
 * Invoice Discount Approval Modal Component
 * Phase 3 Frontend - Preview and approve discounts on invoices
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableRow,
  FormControlLabel,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
  Switch,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  useDiscountPreview,
  useApplyDiscounts,
  useApplyOneOffDiscount,
  useVariableDiscountSettings,
} from '../../../hooks/useDiscounts';
import EligibleDiscountChecklist from '../../invoices/EligibleDiscountChecklist';
import DiscountCoverageFields from '../discounts/DiscountCoverageFields';
import type { Invoice } from '../../../api/invoices.api';
import type { DiscountCoverage } from '../../../types/discount.types';
import {
  COVERS_EVERYTHING,
  coverageFromSchoolDefault,
  coverageIsEmpty,
  estimateNewDiscount,
  normaliseCoverage,
} from '../../../utils/discountCoverage';

interface InvoiceDiscountApprovalModalProps {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  /**
   * #625: the invoice's stored lines and the discounts already on it, so the one-off's "takes
   * off" counts only the lines it covers and the savings card starts from what is already off.
   */
  invoice?: Pick<Invoice, 'InvoiceItem' | 'InvoiceDiscount'>;
}

export default function InvoiceDiscountApprovalModal({
  open,
  onClose,
  invoiceId,
  invoice,
}: InvoiceDiscountApprovalModalProps) {
  const { data: preview, isLoading, error } = useDiscountPreview(invoiceId);
  const applyMutation = useApplyDiscounts(invoiceId);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  // --- One-off discount (2026-09-24) ---
  const oneOffMutation = useApplyOneOffDiscount(invoiceId);
  const [showOneOff, setShowOneOff] = useState(false);
  const [oneOffName, setOneOffName] = useState('');
  // Held as a STRING so the field can be emptied while retyping; coercing per
  // keystroke makes an empty field unclearable.
  const [oneOffValue, setOneOffValue] = useState('');
  const [oneOffIsPercent, setOneOffIsPercent] = useState(true);
  const [oneOffSave, setOneOffSave] = useState(false);
  // #625: what the one-off covers, starting from the school default (D6).
  const settings = useVariableDiscountSettings({ enabled: open });
  const schoolDefault = settings.isSuccess ? coverageFromSchoolDefault(settings.data) : undefined;
  const [oneOffCoverage, setOneOffCoverage] = useState<DiscountCoverage>(COVERS_EVERYTHING);
  // Sent only when changed (/qa phase 5 L2): untouched, the server applies the school default.
  const [oneOffCoverageTouched, setOneOffCoverageTouched] = useState(false);
  // /qa #625 L1: a one-off started before the default arrived takes it when it does, unless changed.
  useEffect(() => {
    if (!settings.data || oneOffCoverageTouched) return;
    setOneOffCoverage(coverageFromSchoolDefault(settings.data));
  }, [settings.data, oneOffCoverageTouched]);

  const parsedOneOffValue = parseFloat(oneOffValue);
  const oneOffValueIsValid =
    Number.isFinite(parsedOneOffValue) &&
    parsedOneOffValue > 0 &&
    // Mirrors the server's ceiling, so a 500% discount is refused here rather than
    // as a 400 after the round trip.
    (!oneOffIsPercent || parsedOneOffValue <= 100);
  const oneOffIsValid = oneOffName.trim().length > 0 && oneOffValueIsValid && !coverageIsEmpty(oneOffCoverage);

  const subtotalNumber = Number(preview?.estimates?.subtotal ?? 0);
  // What the discounts already on the invoice take off (/qa phase 3 I2: the card ignored them).
  const alreadyOff = (invoice?.InvoiceDiscount ?? []).reduce((sum, d) => sum + Number(d.calculatedAmount), 0);
  // #625: the one-off counts only the lines it covers. Without the lines (no `invoice` passed)
  // it falls back to the whole subtotal, as before.
  const estimateLines = invoice
    ? invoice.InvoiceItem.map((i) => ({ kind: i.kind, productCategoryId: i.productCategoryId, amount: Number(i.total) }))
    : [{ kind: 'OTHER' as const, productCategoryId: null, amount: subtotalNumber }];
  const oneOffEstimate =
    oneOffValueIsValid && !coverageIsEmpty(oneOffCoverage)
      ? estimateNewDiscount(estimateLines, invoice ? oneOffCoverage : COVERS_EVERYTHING, parsedOneOffValue, oneOffIsPercent, alreadyOff)
      : null;
  const oneOffPreviewAmount = oneOffEstimate?.amount ?? null;
  const oneOffWouldCap = !!oneOffEstimate?.capped;

  const startOneOff = () => {
    setOneOffCoverage(schoolDefault ?? COVERS_EVERYTHING);
    setOneOffCoverageTouched(false);
    setShowOneOff(true);
  };

  const resetOneOff = () => {
    setShowOneOff(false);
    setOneOffName('');
    setOneOffValue('');
    setOneOffIsPercent(true);
    setOneOffSave(false);
    setOneOffCoverage(COVERS_EVERYTHING);
  };

  const handleApplyOneOff = async () => {
    if (!oneOffIsValid) return;
    try {
      await oneOffMutation.mutateAsync({
        name: oneOffName.trim(),
        value: parsedOneOffValue,
        isPercent: oneOffIsPercent,
        saveToCustomDiscounts: oneOffSave,
        ...(oneOffCoverageTouched ? { coverage: normaliseCoverage(oneOffCoverage) } : {}),
      });
      // ⚠️ CLOSE on success, matching handleApply. Staying open looked harmless but
      // was a double-charge invitation: the savings card below is computed from
      // `preview.availableDiscounts` filtered by `selectedIds`, and an applied one-off
      // is in neither — so the card kept reading -$0.00 and the admin got NO feedback
      // that anything happened. Measured during /qa: a second identical submit was
      // accepted and created a second InvoiceDiscount row.
      resetOneOff();
      onClose();
    } catch {
      // Surfaced by the hook's snackbar. Swallowed here so the rejection is not
      // unhandled (measured: 1 unhandled rejection before this) and so resetOneOff()
      // does NOT run — the admin's typing survives a failed submit.
    }
  };

  // What the ticked discounts take off. Each was priced ALONE beside the discounts already on the
  // invoice; ticked together they share what is left, so the sum is capped at it (an estimate:
  // approve settles them together, /qa phase 3 I2).
  const selectedSum = useMemo(() => {
    if (!preview?.availableDiscounts) return 0;
    return preview.availableDiscounts
      .filter((d) => selectedIds.includes(d.id))
      .reduce((sum, d) => sum + (Number(d.calculatedAmount) || 0), 0);
  }, [preview, selectedIds]);
  const left = Math.max(0, (preview?.estimates.subtotal ?? 0) - alreadyOff);
  const totalDiscount = Math.min(selectedSum, left);

  const handleSelectDiscount = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter((sid) => sid !== id));
    }
  };

  const handleApply = async () => {
    try {
      await applyMutation.mutateAsync({
        selectedDiscountIds: selectedIds,
      });
      onClose();
    } catch (error) {
      // Error handled by hook snackbar
    }
  };

  const handleClose = () => {
    if (selectedIds.length > 0) {
      const confirmed = window.confirm(
        'You have selected discounts but not applied them. Close anyway?'
      );
      if (!confirmed) return;
    }
    setSelectedIds([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Apply Discounts to Invoice</DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error ? (
          <Alert severity="error">
            Failed to load discount preview. Please try again.
          </Alert>
        ) : isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : preview ? (
          <Stack spacing={3}>
            {/* Invoice Summary */}
            <Card sx={{ bgcolor: 'action.hover' }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Invoice Summary
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Current Subtotal</TableCell>
                      <TableCell align="right">
                        <strong>${preview.estimates.subtotal.toFixed(2)}</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Available Discounts */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Available Discounts
              </Typography>

              {preview.availableDiscounts.length === 0 ? (
                <Alert severity="info">
                  No discounts available for this invoice
                </Alert>
              ) : (
                <EligibleDiscountChecklist
                  discounts={preview.availableDiscounts}
                  selectedIds={selectedIds}
                  onToggle={handleSelectDiscount}
                  showAmounts
                />
              )}
            </Box>

            <Divider />

            {/* One-off discount (2026-09-24). Its own submit: the Apply button below
                posts `selectedDiscountIds` and is disabled on an empty selection, so
                it cannot carry a typed discount. */}
            <Box>
              {!showOneOff ? (
                <Button
                  startIcon={<AddIcon />}
                  onClick={startOneOff}
                  size="small"
                >
                  Add a one-off discount
                </Button>
              ) : (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      One-off discount
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Applies to this invoice only, unless you save it below.
                    </Typography>

                    <Stack spacing={2} sx={{ mt: 2 }}>
                      <TextField
                        label="Label"
                        size="small"
                        fullWidth
                        value={oneOffName}
                        onChange={(e) => setOneOffName(e.target.value)}
                        placeholder="e.g. Goodwill adjustment"
                        inputProps={{ maxLength: 200 }}
                      />

                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <TextField
                          label="Value"
                          size="small"
                          type="number"
                          value={oneOffValue}
                          onChange={(e) => setOneOffValue(e.target.value)}
                          error={oneOffValue.trim() !== '' && !oneOffValueIsValid}
                          helperText={
                            oneOffValue.trim() !== '' && !oneOffValueIsValid
                              ? oneOffIsPercent
                                ? 'Enter a percentage above 0 and up to 100'
                                : 'Enter an amount above 0'
                              : ' '
                          }
                          InputProps={{
                            startAdornment: !oneOffIsPercent ? (
                              <InputAdornment position="start">$</InputAdornment>
                            ) : undefined,
                            endAdornment: oneOffIsPercent ? (
                              <InputAdornment position="end">%</InputAdornment>
                            ) : undefined,
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ width: 180 }}
                        />
                        <FormControlLabel
                          sx={{ mt: 0.5 }}
                          control={
                            <Switch
                              checked={oneOffIsPercent}
                              onChange={(e) => setOneOffIsPercent(e.target.checked)}
                            />
                          }
                          label={oneOffIsPercent ? 'Percentage' : 'Fixed amount'}
                        />
                      </Stack>

                      <DiscountCoverageFields
                        value={oneOffCoverage}
                        onChange={(next) => {
                          setOneOffCoverage(next);
                          setOneOffCoverageTouched(true);
                        }}
                        defaultCoverage={schoolDefault}
                      />

                      {oneOffPreviewAmount !== null && (
                        <Typography variant="body2" color="text.secondary" data-testid="one-off-estimate">
                          Takes off about <strong>${oneOffPreviewAmount.toFixed(2)}</strong>
                          {oneOffWouldCap && ' — capped at what the lines it covers have left'}
                          {/* /qa phase 5 L3: the estimate does not settle overlaps; the server does. */}
                          {alreadyOff > 0 && ' (less if a discount already on the invoice covers the same lines)'}
                        </Typography>
                      )}

                      <FormControlLabel
                        control={
                          <Switch
                            checked={oneOffSave}
                            onChange={(e) => setOneOffSave(e.target.checked)}
                          />
                        }
                        label="Also save to custom discounts"
                      />
                      {oneOffSave && (
                        <Typography variant="caption" color="text.secondary">
                          It will then be offered on future invoices too.
                        </Typography>
                      )}

                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={!oneOffIsValid || oneOffMutation.isPending}
                          onClick={handleApplyOneOff}
                        >
                          {oneOffMutation.isPending ? 'Applying…' : 'Apply discount'}
                        </Button>
                        <Button
                          size="small"
                          onClick={resetOneOff}
                          disabled={oneOffMutation.isPending}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Box>

            {/* Savings Calculation Summary */}
            <Card sx={{ bgcolor: 'success.light', border: '2px solid #4caf50' }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Savings Breakdown
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Subtotal</TableCell>
                      <TableCell align="right">
                        ${preview.estimates.subtotal.toFixed(2)}
                      </TableCell>
                    </TableRow>
                    {alreadyOff > 0 && (
                      <TableRow>
                        <TableCell>Discounts already applied</TableCell>
                        <TableCell align="right">-${alreadyOff.toFixed(2)}</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {alreadyOff > 0 ? 'Selected discounts (about)' : 'Total Discounts'}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color: 'success.main',
                        }}
                      >
                        -${totalDiscount.toFixed(2)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.1rem',
                          pt: 1.5,
                          borderTop: '2px solid #4caf50',
                        }}
                      >
                        New Subtotal
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.1rem',
                          pt: 1.5,
                          borderTop: '2px solid #4caf50',
                        }}
                      >
                        ${Math.max(
                          0,
                          preview.estimates.subtotal - alreadyOff - totalDiscount
                        ).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={
            selectedIds.length === 0 ||
            applyMutation.isPending ||
            !preview?.availableDiscounts.length
          }
          onClick={handleApply}
          startIcon={applyMutation.isPending && <CircularProgress size={20} />}
        >
          {applyMutation.isPending ? 'Applying...' : 'Apply Discounts'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
